import React from "react";
import { useSelector } from "react-redux";

import useDomain from "hooks/useDomain";
import { getDomainCountryCode } from "redux/selectors/config";
import {
  getCurrentDispensary,
  getCurrentDispensaryCitySlug,
  getCurrentDispensaryStateName,
  getCurrentDispensaryStateSlug,
} from "redux/selectors/dispensary";
import { getRetailerInfo } from "utils/getRetailerInfo";
import { retailCopyFormatter } from "utils/retailCopyFormatter";

import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";

const DispensaryBreadcrumbs: React.FC<{
  product?: string;
}> = ({ product }) => {
  const { city, deliveryEnabled, name, pickupEnabled, retailType, slug } =
    useSelector(getCurrentDispensary) || {};
  const countryCode = useSelector(getDomainCountryCode);
  const isDeliveryOnly = !pickupEnabled && deliveryEnabled;
  const isDispensaryRetailer = retailType === "dispensary";
  const showDeliveryBreadcrumbs = isDispensaryRetailer && isDeliveryOnly;

  const citySlug = useSelector(getCurrentDispensaryCitySlug);
  const state = useSelector(getCurrentDispensaryStateName);
  const stateSlug = useSelector(getCurrentDispensaryStateSlug);

  const baseUrl = useDomain();

  const retailerInfo = getRetailerInfo(countryCode, retailType);
  const { copy, finderUrl, url } = retailerInfo;
  const breadcrumbUrl = showDeliveryBreadcrumbs ? "/delivery" : finderUrl;

  const retailerUrl = `${baseUrl}${url}/${slug}`;
  const retailerBreadcrumb = showDeliveryBreadcrumbs
    ? "Delivery"
    : retailCopyFormatter(copy, "all");

  return (
    <Breadcrumbs>
      <Breadcrumb href={breadcrumbUrl} testId="breadcrumb-retailer">
        {retailerBreadcrumb}
      </Breadcrumb>

      {!!stateSlug && (
        <Breadcrumb
          href={`${breadcrumbUrl}/${stateSlug}`}
          testId="breadcrumb-state"
        >
          {state}
        </Breadcrumb>
      )}

      {!!citySlug && (
        <Breadcrumb
          href={`${breadcrumbUrl}/${stateSlug}/${citySlug}`}
          testId="breadcrumb-city"
        >
          {city}
        </Breadcrumb>
      )}

      {!!name && (
        <Breadcrumb
          href={retailerUrl}
          currentLocation={!product}
          testId="breadcrumb-name"
        >
          {name}
        </Breadcrumb>
      )}

      {!!product && (
        <Breadcrumb currentLocation={true} testId="breadcrumb-product">
          {product}
        </Breadcrumb>
      )}
    </Breadcrumbs>
  );
};

export default DispensaryBreadcrumbs;
